import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import axios from '../../utils/axiosConfig'
import { useModal } from 'context/ModalContext'
import { peekPostAuthRedirect, takePostAuthRedirect } from 'utils/postAuthRedirect'

// Heroicons (outline)
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

import { ArrowPathIcon } from '@heroicons/react/24/solid'

const EmailConfirmation = () => {
  const [status, setStatus] = useState('loading')
  const { t } = useTranslation()
  const { showLoginModal } = useModal()
  // Set when the brand signed up from an event invite.
  const [nextPath] = useState(() => peekPostAuthRedirect())

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const role = searchParams.get('role')

  useEffect(() => {
    const confirmEmail = async () => {
      try {
        await axios.post('/auth/confirm-email', { token, role })
        setStatus('success')
      } catch (err) {
        console.error('Email confirmation failed:', err)
        setStatus('error')
      }
    }

    if (token && role) {
      confirmEmail()
    } else {
      setStatus('invalid')
    }
  }, [token, role])

  const handleRedirect = () => {
    if (status === 'success' && nextPath) {
      takePostAuthRedirect()
      navigate(nextPath)
      showLoginModal(nextPath)
      return
    }
    navigate('/')
  }

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return {
          icon: (
            <ArrowPathIcon className="w-10 h-10 text-blue-500 animate-spin" />
          ),
          message: 'Confirming your email...',
        }
      case 'success':
        return {
          icon: <CheckCircleIcon className="w-10 h-10 text-green-500" />,
          message: 'Email confirmed! You can now log in.',
        }
      case 'error':
        return {
          icon: <XCircleIcon className="w-10 h-10 text-red-500" />,
          message: 'Something went wrong. Please try again later.',
        }
      case 'invalid':
        return {
          icon: (
            <ExclamationTriangleIcon className="w-10 h-10 text-yellow-500" />
          ),
          message: 'Invalid confirmation link.',
        }
      default:
        return null
    }
  }

  const { icon, message } = renderContent()

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
        <div className="flex justify-center mb-4">{icon}</div>
        <p className="text-lg font-medium text-gray-700 mb-6">{message}</p>
        <button
          onClick={handleRedirect}
          disabled={status === 'loading'}
          className={`inline-flex items-center px-4 py-2 rounded-xl transition ${
            status === 'loading'
              ? 'bg-yellow-05/20 text-white cursor-not-allowed'
              : 'bg-yellow-05/80 text-white font- hover:bg-yellow-05'
          }`}
        >
          {status === 'success' && nextPath
            ? t('confirm_email.continue_to_event', 'Log in and go to the event')
            : 'Go to Homepage'}
        </button>
      </div>
    </div>
  )
}

export default EmailConfirmation
